import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Typography, Card, Row, Col, Spin, notification, Statistic, DatePicker, Button, Space, Empty, Tag } from 'antd';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import axiosClient from '../../api/axiosClient';
import { 
    ArrowUpOutlined, ArrowDownOutlined, FilterOutlined, 
    ReloadOutlined, HistoryOutlined, DashboardOutlined,
    DotChartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { APPBACKGROUND_COLOR } from '../appsettings';

const { RangePicker } = DatePicker;

// Register ChartJS modules
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const { Title: TypographyTitle, Text } = Typography;

const MemberMetrics = () => {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async (from = '', to = '') => {
    setLoading(true);
    try {
      const params = {};
      if (from && to) {
        params.from = from;
        params.to = to;
      }
      const response = await axiosClient.get('/portal/get_metrics.php', { params });
      if (response.data.status === 'success') {
        setMetrics(response.data.data);
      } else {
        notification.error({ message: "Failed to load metrics", description: response.data.message });
      }
    } catch (error) {
      console.error(error);
      notification.error({ message: "Network Error", description: "Could not fetch metrics data." });
    } finally {
      setLoading(false);
    }
  };

  const onDateChange = (dates) => {
    if (dates) {
      const from = dates[0].format('YYYY-MM-DD');
      const to = dates[1].format('YYYY-MM-DD');
      setDateRange(dates);
      fetchMetrics(from, to);
    } else {
      setDateRange(null);
      fetchMetrics();
    }
  };

  const handleQuickFilter = (days) => {
    const end = dayjs();
    const start = dayjs().subtract(days, 'day');
    const range = [start, end];
    setDateRange(range);
    fetchMetrics(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
  };

  const resetFilters = () => {
    setDateRange(null);
    fetchMetrics();
  };

  // Calculations for stats & trends
  const analytics = useMemo(() => {
    if (metrics.length === 0) return null;
    
    const latest = metrics[metrics.length - 1];
    const first = metrics[0];

    // Helper for diffs
    const getDiff = (key) => (parseFloat(latest[key]) - parseFloat(first[key])).toFixed(1);
    
    return {
      latest,
      weightDiff: getDiff('weight'),
      bmiDiff: getDiff('bmi'),
      bodyFatDiff: getDiff('body_fat'),
      muscleMassDiff: getDiff('muscle_mass'),
      isWeightDown: (latest.weight - first.weight) <= 0,
      isFatDown: (latest.body_fat - first.body_fat) <= 0,
      isMuscleUp: (latest.muscle_mass - first.muscle_mass) >= 0,
      isBmiDown: (latest.bmi - first.bmi) <= 0,
    };
  }, [metrics]);

  // Chart Data preparation
  const weightData = useMemo(() => ({
    labels: metrics.map(m => m.log_date),
    datasets: [
      {
        fill: true,
        label: 'Body Weight (kg)',
        data: metrics.map(m => m.weight),
        borderColor: '#209c96',
        backgroundColor: (context) => {
            const chart = context.chart;
            const {ctx, chartArea} = chart;
            if (!chartArea) return null;
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, 'rgba(32, 156, 150, 0.2)');
            gradient.addColorStop(1, 'rgba(32, 156, 150, 0)');
            return gradient;
        },
        tension: 0.4,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#209c96',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  }), [metrics]);

  const bodyCompData = useMemo(() => ({
    labels: metrics.map(m => m.log_date),
    datasets: [
      {
        label: 'Body Fat (%)',
        data: metrics.map(m => m.body_fat),
        backgroundColor: 'rgba(255, 99, 132, 0.7)',
        borderRadius: 8,
        hoverBackgroundColor: 'rgba(255, 99, 132, 0.9)',
      },
      {
        label: 'Muscle Mass (%)',
        data: metrics.map(m => m.muscle_mass),
        backgroundColor: 'rgba(32, 156, 150, 0.7)',
        borderRadius: 8,
        hoverBackgroundColor: 'rgba(32, 156, 150, 0.9)',
      },
    ],
  }), [metrics]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: 'top',
        labels: { font: { family: 'Inter', weight: 600 } }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(255,255,255,0.95)',
        titleColor: '#333',
        bodyColor: '#666',
        borderColor: '#eee',
        borderWidth: 1,
        titleFont: { size: 14, family: 'Inter', weight: 700 },
        bodyFont: { size: 14, family: 'Inter' },
        padding: 12,
        cornerRadius: 12,
        usePointStyle: true,
        callbacks: {
          label: (context) => ` ${context.dataset.label}: ${context.parsed.y}${context.dataset.label.includes('%') ? '%' : ' kg'}`
        }
      },
    },
    scales: {
      y: { 
        grid: { borderDash: [5, 5], color: 'rgba(0,0,0,0.03)' },
        ticks: { font: { family: 'Inter' } }
      },
      x: { 
        grid: { display: false },
        ticks: { font: { family: 'Inter' } }
      },
    },
    interaction: { mode: 'nearest', axis: 'x', intersect: false },
  }), []);

  const glassStyle = {
    background: 'rgba(255, 255, 255, 0.4)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.8)',
    borderRadius: '24px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.05)',
  };

  return (
    <div style={{ width: '100%', paddingBottom: 40 }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 32 }}>
            <div>
                <TypographyTitle level={2} style={{ color: '#1a1a1a', fontWeight: 800, margin: 0, fontFamily: "'Inter', sans-serif" }}>
                    My Progress <Text type="secondary" style={{ fontSize: '0.6em', fontWeight: 400 }}>Analytics</Text>
                </TypographyTitle>
                <p style={{ color: '#666', fontSize: '16px', margin: '8px 0 0' }}>
                    Visualizing your health journey with high-fidelity performance tracking.
                </p>
            </div>
            <Space size="small">
                <Button 
                    icon={<ReloadOutlined />} 
                    onClick={resetFilters}
                    className="glass-btn"
                    style={{ borderRadius: 12 }}
                >
                    All Time
                </Button>
                <Button 
                    onClick={() => handleQuickFilter(7)}
                    className="glass-btn"
                    style={{ borderRadius: 12 }}
                >
                    7d
                </Button>
                <Button 
                    onClick={() => handleQuickFilter(30)}
                    className="glass-btn"
                    style={{ borderRadius: 12 }}
                >
                    30d
                </Button>
                <Button 
                    onClick={() => handleQuickFilter(90)}
                    className="glass-btn"
                    style={{ borderRadius: 12 }}
                >
                    90d
                </Button>
            </Space>
        </div>

        {/* Filter Card */}
        <Card style={{ ...glassStyle, marginBottom: 32 }} bodyStyle={{ padding: '16px 24px' }} bordered={false}>
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Space direction="horizontal" size="middle" wrap>
                <Text strong style={{ color: '#555' }}><FilterOutlined /> Custom Range:</Text>
                <RangePicker 
                  value={dateRange} 
                  onChange={onDateChange} 
                  style={{ borderRadius: 12, border: '1px solid #eee', boxShadow: 'none' }}
                />
              </Space>
            </Col>
            <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                <Tag icon={<HistoryOutlined />} color="processing" style={{ borderRadius: 6, padding: '4px 12px' }}>
                    Tracking since {metrics[0]?.log_date || 'N/A'}
                </Tag>
            </Col>
          </Row>
        </Card>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '100px' }}>
            <Spin size="large" tip="Processing analytics..." />
          </div>
        ) : metrics.length === 0 ? (
          <Card style={glassStyle} bodyStyle={{ padding: 60 }}>
            <Empty 
                description={
                    <span>No metrics found for this period. <br /> Try adjusting your filters or <a onClick={resetFilters}>show all time</a>.</span>
                } 
            />
          </Card>
        ) : (
          <>
            {/* Analytics Stats Grid */}
            <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
              <Col xs={24} sm={12} lg={6}>
                <Card style={glassStyle} bodyStyle={{ padding: 20 }} hoverable>
                  <Statistic
                    title={<Space><DashboardOutlined /> Weight</Space>}
                    value={analytics.latest.weight}
                    precision={1}
                    valueStyle={{ color: '#1a1a1a', fontWeight: '800' }}
                    suffix=" kg"
                  />
                  <Text type={analytics.isWeightDown ? "success" : "danger"} style={{ fontSize: 12 }}>
                    {analytics.isWeightDown ? <ArrowDownOutlined /> : <ArrowUpOutlined />} {Math.abs(analytics.weightDiff)} kg total change
                  </Text>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card style={glassStyle} bodyStyle={{ padding: 20 }} hoverable>
                  <Statistic
                    title={<Space><DotChartOutlined /> BMI</Space>}
                    value={analytics.latest.bmi}
                    precision={1}
                    valueStyle={{ color: '#1a1a1a', fontWeight: '800' }}
                  />
                  <Text type={analytics.isBmiDown ? "success" : "warning"} style={{ fontSize: 12 }}>
                    {analytics.isBmiDown ? "Improving trend" : "Staying level"}
                  </Text>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card style={glassStyle} bodyStyle={{ padding: 20 }} hoverable>
                  <Statistic
                    title="Body Fat"
                    value={analytics.latest.body_fat}
                    precision={1}
                    valueStyle={{ color: analytics.isFatDown ? '#3f8600' : '#fa8c16' }}
                    suffix="%"
                  />
                  <Text type={analytics.isFatDown ? "success" : "warning"} style={{ fontSize: 12 }}>
                    {analytics.isFatDown ? <ArrowDownOutlined /> : <ArrowUpOutlined />} {Math.abs(analytics.bodyFatDiff)}% change
                  </Text>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card style={glassStyle} bodyStyle={{ padding: 20 }} hoverable>
                  <Statistic
                    title="Muscle Mass"
                    value={analytics.latest.muscle_mass}
                    precision={1}
                    valueStyle={{ color: analytics.isMuscleUp ? '#209c96' : '#fa8c16' }}
                    suffix="%"
                  />
                  <Text type={analytics.isMuscleUp ? "success" : "danger"} style={{ fontSize: 12 }}>
                    {analytics.isMuscleUp ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(analytics.muscleMassDiff)}% trend
                  </Text>
                </Card>
              </Col>
            </Row>

            {/* Visual Analytics */}
            <Row gutter={[24, 24]}>
              <Col xs={24} xl={15}>
                <Card style={glassStyle} bordered={false} bodyStyle={{ padding: 24 }}>
                  <TypographyTitle level={4} style={{ color: '#333', marginBottom: 24 }}>Weight Progression</TypographyTitle>
                  <div style={{ height: '350px' }}>
                    <Line data={weightData} options={chartOptions} />
                  </div>
                </Card>
              </Col>

              <Col xs={24} xl={9}>
                <Card style={glassStyle} bordered={false} bodyStyle={{ padding: 24 }}>
                  <TypographyTitle level={4} style={{ color: '#333', marginBottom: 24 }}>Body Comp Matrix</TypographyTitle>
                  <div style={{ height: '350px' }}>
                    <Bar data={bodyCompData} options={chartOptions} />
                  </div>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
      
      <style>{`
        .glass-btn {
            background: rgba(255, 255, 255, 0.4) !important;
            backdrop-filter: blur(10px) !important;
            border: 1px solid rgba(255, 255, 255, 0.6) !important;
            box-shadow: 0 4px 12px rgba(0,0,0,0.03);
            transition: all 0.3s;
        }
        .glass-btn:hover {
            background: #fff !important;
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(0,0,0,0.08);
            border-color: #209c96 !important;
            color: #209c96 !important;
        }
      `}</style>
    </div>
  );
};

export default MemberMetrics;
